import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { rawApi, rawApiFile } from '../api/client';
import { listKey } from '../api/queryKeys';
import { CrudSection } from '../components/CrudSection';
import { EntityLabel, EntitySelect } from '../components/EntityField';
import { EMISSION_POINTS_PATH as POINTS_PATH, RangeWarnings } from '../components/RangeWarnings';
import { useEntityIndex } from '../hooks/useEntityIndex';
import { useBusinessDate, useLocationId, useLocationTimezone } from '../hooks/useSeedLocation';
import { enumOptionsFor } from '../lib/enums';
import { formatMoney, toMajorUnits, toMinorUnits, zonedDayRange } from '../lib/format';
import { notifyDone, notifyError } from '../lib/notify';

const DOCUMENTS_PATH = '/admin/v1/fiscal/documents';
const SETTINGS_PATH = '/admin/v1/admin/settings';

/** The threshold above which a guest has to give an RTN before the bill can be printed. */
const RTN_THRESHOLD_KEY = 'fiscal.customerRtnRequiredAboveMinor';
const RTN_THRESHOLD_CURRENCY_KEY = 'fiscal.customerRtnRequiredCurrency';

/**
 * The RTN, as it is written on paperwork and as it has to be stored.
 *
 * Fourteen digits, and nothing more. No department-code check and no invented check digit: a
 * validator that rejects a real RTN is worse than one that lets a typo through, because the guest
 * is standing at the counter waiting and nobody can argue with a rule. Dashes come off on the way
 * in, because the letter from the tax office prints them and the field stores digits.
 */
function normaliseRtn(raw: unknown): string {
  return String(raw ?? '').replace(/[\s-]/g, '');
}

function rtnProblem(raw: unknown): string | null {
  return /^\d{14}$/.test(normaliseRtn(raw)) ? null : 'An RTN is 14 digits';
}

/**
 * The one part of a registered range that is meant to change day to day.
 *
 * CAI and RTN stay as they are — they are what SAR issues and what gets printed on the invoice, so
 * translating them would leave the screen disagreeing with the paperwork in Dan's hand. Everything
 * AROUND them is English, because this site is.
 */
function IssuingToggle({ point, refresh }: { point: Record<string, unknown>; refresh: () => void }) {
  const active = Boolean(point.active);
  const name = `Register ${String(point.emissionPointCode)}`;

  const toggle = useMutation({
    mutationFn: () => rawApi('PATCH', `${POINTS_PATH}/${String(point.id)}`, { active: !active }),
    onSuccess: () => {
      refresh();
      notifyDone(
        active ? 'Stopped' : 'Printing again',
        active ? `${name} will not print any more invoices.` : `${name} can print invoices again.`,
      );
    },
    onError: (err) => notifyError(err, active ? 'stop this number range' : 'start this number range again'),
  });

  return (
    <Button size="xs" variant="subtle" loading={toggle.isPending} onClick={() => toggle.mutate()}>
      {active ? 'Stop printing' : 'Start printing'}
    </Button>
  );
}

/**
 * Every document ever issued, deliberately unfiltered.
 *
 * Two things are worked out from this and both are wrong if it only holds what is on screen: how
 * much of an invoice has already been given back, and which invoice a credit note points at. A
 * credit note raised next week corrects an invoice from last month, so a date-filtered list would
 * offer to refund money that has already gone back, and would show a credit note referring to
 * "an invoice not on this list" when the invoice is perfectly well on file.
 */
function useAllDocuments() {
  const { data } = useQuery({
    queryKey: listKey(DOCUMENTS_PATH),
    queryFn: () => rawApi<Record<string, unknown>[]>('GET', DOCUMENTS_PATH),
  });
  return data ?? [];
}

/**
 * Every credit note ever issued, asked for on its own.
 *
 * The register above comes back 500 rows deep, newest first — a couple of busy weeks. Ask it for
 * everything and then look up an invoice from March, and neither that invoice nor the credit notes
 * against it are in the answer: the screen concludes nothing has gone back and offers the whole
 * amount again. Credit notes are a handful a month, so 500 of those reaches back years. The server
 * now refuses an over-credit outright; this is what keeps the screen from proposing one.
 */
const CREDIT_NOTES_QUERY = { type: 'creditNote' } as const;

function useCreditNotes() {
  const { data } = useQuery({
    queryKey: listKey(DOCUMENTS_PATH, CREDIT_NOTES_QUERY),
    queryFn: () => rawApi<Record<string, unknown>[]>('GET', `${DOCUMENTS_PATH}?type=creditNote`),
  });
  return data ?? [];
}

/**
 * How much of an invoice has already been given back.
 *
 * An invoice may be credited more than once — a returned bottle today, a disputed line next week —
 * so the form offers the remainder rather than the original amount, which is the difference
 * between correcting a sale and refunding it twice.
 */
function creditedAgainst(documents: Record<string, unknown>[], invoiceId: string): number {
  return documents
    .filter((d) => d.type === 'creditNote' && d.referencesDocumentId === invoiceId)
    .reduce((sum, d) => sum + Math.abs(Number(d.totalMinor ?? 0)), 0);
}

/**
 * What this row IS, in one cell.
 *
 * A credit note used to show as "Credit note, L -500.00" and nothing else, so the question anyone
 * opens this list with — which sale is this taking money off? — could not be answered from the
 * screen at all, even though the link is stored on the row. The other direction was just as blind:
 * an invoice that had already been half refunded looked exactly like one that had not.
 */
function KindCell({
  row,
  documents,
  creditNotes,
}: {
  row: Record<string, unknown>;
  documents: Record<string, unknown>[];
  creditNotes: Record<string, unknown>[];
}) {
  const currency = String(row.currency ?? 'HNL');
  if (row.type === 'creditNote') {
    const original = documents.find((d) => d.id === row.referencesDocumentId);
    return (
      <div>
        <Text size="sm">Credit note</Text>
        <Text size="xs" c="dimmed">
          {original?.correlativo
            ? `against invoice ${String(original.correlativo)}`
            : 'the invoice it corrects is not on file'}
        </Text>
      </div>
    );
  }
  if (row.type !== 'invoice') {
    return <Text size="sm">{row.type === 'debitNote' ? 'Debit note' : 'Deposit receipt'}</Text>;
  }
  const credited = creditedAgainst(creditNotes, String(row.id));
  return (
    <div>
      <Text size="sm">Invoice</Text>
      {credited > 0 && (
        <Text size="xs" c="orange.8">
          {formatMoney(credited, currency)} credited back
        </Text>
      )}
    </div>
  );
}

interface StoredSetting {
  key: string;
  value: unknown;
}

/**
 * The amount above which a guest has to give their RTN before the bill can be printed.
 *
 * It lives HERE and not on the Settings screen because it is a fiscal rule, and the person setting
 * it is looking at the CAI paperwork when they set it. Burying it in a list of house rules between
 * the tab limit and the kitchen ticket language would put it where nobody looking for it would
 * think to look.
 *
 * Empty is the shipped state and it means never ask. Most guests never give an RTN, and treating
 * the customer as required gets the shape of this business backwards — the threshold the tax
 * office sets is a number the owner's accountant supplies, and inventing one would put a refusal
 * in front of a paying guest on the strength of a guess.
 */
function CustomerRtnThreshold() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: listKey(SETTINGS_PATH),
    queryFn: () => rawApi<StoredSetting[]>('GET', SETTINGS_PATH),
  });
  const byKey = new Map((data ?? []).map((s) => [s.key, s.value]));
  const storedMinor = byKey.get(RTN_THRESHOLD_KEY);
  const storedCurrency = String(byKey.get(RTN_THRESHOLD_CURRENCY_KEY) ?? 'USD');

  const [amount, setAmount] = useState<number | string>('');
  const [currency, setCurrency] = useState<string>('USD');
  const [touched, setTouched] = useState(false);

  // Seeded from the server once, then left alone: re-seeding on every refetch would wipe out what
  // somebody is halfway through typing.
  useEffect(() => {
    if (touched || isLoading) return;
    setAmount(typeof storedMinor === 'number' ? toMajorUnits(storedMinor) : '');
    setCurrency(storedCurrency);
  }, [touched, isLoading, storedMinor, storedCurrency]);

  const save = useMutation({
    mutationFn: async () => {
      const minor = amount === '' ? null : (toMinorUnits(amount) ?? null);
      await rawApi('PUT', `${SETTINGS_PATH}/${RTN_THRESHOLD_KEY}`, { value: minor });
      await rawApi('PUT', `${SETTINGS_PATH}/${RTN_THRESHOLD_CURRENCY_KEY}`, { value: currency });
      return minor;
    },
    onSuccess: (minor) => {
      setTouched(false);
      void queryClient.invalidateQueries({ queryKey: listKey(SETTINGS_PATH) });
      notifyDone(
        'Saved',
        minor === null
          ? 'Nobody will be asked for an RTN. A guest who wants one on their bill can still give it.'
          : `Bills over ${formatMoney(minor, currency)} will need the customer's RTN before they can be printed.`,
      );
    },
    onError: (err) => notifyError(err, 'save this amount'),
  });

  return (
    <Paper withBorder p="md" radius="md" mb="md">
      <Title order={5}>Ask for the customer&rsquo;s RTN above</Title>
      <Text size="sm" c="dimmed" mt={2} mb="md">
        Most guests never give one, and they do not have to. Leave this empty to never ask. Your accountant
        will tell you the amount the tax office sets.
      </Text>
      <Group gap="sm" align="flex-end">
        <NumberInput
          label="Amount"
          placeholder="Never ask"
          min={0}
          decimalScale={2}
          fixedDecimalScale
          thousandSeparator=","
          hideControls
          w={200}
          value={amount}
          onChange={(v) => {
            setTouched(true);
            setAmount(v);
          }}
        />
        <EntitySelectCurrency
          value={currency}
          onChange={(v) => {
            setTouched(true);
            setCurrency(v);
          }}
        />
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
      </Group>
    </Paper>
  );
}

/** The two currencies this bar transacts in, as a picker. */
function EntitySelectCurrency({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Select
      label="In"
      data={enumOptionsFor('currency', ['USD', 'HNL'])}
      value={value}
      onChange={(v) => v && onChange(v)}
      allowDeselect={false}
      comboboxProps={{ withinPortal: true }}
      w={140}
    />
  );
}

/**
 * The kept copy of a printed document.
 *
 * The guest's paper came off the register's own printer at the moment of the sale; this is the file the
 * business has to be able to produce years later, and the reason it exists at all is that the tax
 * authority can ask for one particular number long after everyone has forgotten the evening.
 *
 * It is rendered after the fact rather than on the sale, so for a few seconds after a document is
 * issued there is nothing to show yet. That is said plainly instead of failing, because "still
 * being prepared" and "lost" are very different things to be told about a legal document — and
 * asking for it is itself what starts the render again if one was ever missed.
 */
function DocumentCopyButton({ doc }: { doc: Record<string, unknown> }) {
  const number = doc.correlativo ? String(doc.correlativo) : 'this document';
  const open = useMutation({
    mutationFn: async () => {
      const blob = await rawApiFile(`${DOCUMENTS_PATH}/${String(doc.id)}/pdf`);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.target = '_blank';
      anchor.rel = 'noopener';
      anchor.download = `${number}.pdf`;
      anchor.click();
      // Long enough for the tab to have taken the bytes; the object would otherwise be held for
      // the life of the page every time somebody opens a document.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    onError: (err) => notifyError(err, `open the copy of ${number}`),
  });

  if (!doc.correlativo) return null;
  return (
    <Button size="xs" variant="subtle" loading={open.isPending} onClick={() => open.mutate()}>
      {doc.pdfR2Key ? 'View' : 'Still being prepared'}
    </Button>
  );
}

/**
 * Correcting an issued invoice.
 *
 * Honduran fiscal rules are the reason this exists: an invoice that has been given a CAI and a
 * correlativo is final, and the only lawful way to take money back off it is a separate numbered
 * document from a range registered for credit notes. That is also why the range is chosen rather
 * than assumed — printing a credit note out of the invoice range is precisely the mistake the
 * rules are written to prevent.
 */
function CreditNoteButton({ doc }: { doc: Record<string, unknown> }) {
  const [opened, setOpened] = useState(false);
  const queryClient = useQueryClient();
  const ranges = useEntityIndex('creditNoteRange');
  const reasons = useEntityIndex('creditNoteReason');

  const invoiceId = String(doc.id);
  const currency = String(doc.currency ?? 'HNL');
  const number = doc.correlativo ? String(doc.correlativo) : 'this invoice';
  const totalMinor = Number(doc.totalMinor ?? 0);
  const creditedMinor = creditedAgainst(useCreditNotes(), invoiceId);
  const remainingMinor = Math.max(0, totalMinor - creditedMinor);

  const form = useForm({
    initialValues: {
      emissionPointId: '',
      reasonCode: '',
      amount: remainingMinor / 100,
      detail: '',
    },
    validate: {
      emissionPointId: (v) => (v ? null : 'Choose which credit note range this comes out of'),
      reasonCode: (v) => (v ? null : 'Choose why the money is going back'),
      amount: (v) => {
        const minor = toMinorUnits(v);
        if (!minor || minor <= 0) return 'Enter how much to give back';
        if (minor > remainingMinor) {
          return `That is more than is left on this invoice — at most ${formatMoney(remainingMinor, currency)}`;
        }
        return null;
      },
    },
  });

  // Seeded on opening rather than at mount: after one credit note is issued the row re-renders
  // with a smaller remainder, and a form still offering the original amount would be inviting a
  // second refund of money that has already gone back.
  const open = () => {
    form.setValues({ emissionPointId: '', reasonCode: '', amount: remainingMinor / 100, detail: '' });
    form.clearErrors();
    setOpened(true);
  };

  const close = () => {
    setOpened(false);
    form.reset();
  };

  const issue = useMutation({
    mutationFn: (v: { emissionPointId: string; reasonCode: string; amount: number; detail: string }) =>
      rawApi('POST', '/admin/v1/fiscal/credit-notes', {
        invoiceDocumentId: invoiceId,
        emissionPointId: v.emissionPointId,
        amountMinor: toMinorUnits(v.amount),
        reasonCode: v.reasonCode,
        ...(v.detail.trim() ? { detail: v.detail.trim() } : {}),
      }),
    onSuccess: (_result, v) => {
      // Dropped by prefix rather than through the table's own refresh, which only knows the days
      // that are picked. With a day chosen the register is held twice — once for that day, once
      // unfiltered — and the unfiltered copy is the one that works out how much of an invoice has
      // already gone back. Left stale, the row went on offering the whole amount again, which is
      // a second refund of money that had already been returned.
      void queryClient.invalidateQueries({ queryKey: listKey(DOCUMENTS_PATH) });
      // Issuing burns a number, so "numbers left" on the other tab is now one out of date.
      void queryClient.invalidateQueries({ queryKey: listKey(POINTS_PATH) });
      notifyDone(
        'Credit note issued',
        `${formatMoney(toMinorUnits(v.amount) ?? 0, currency)} has been credited against invoice ${number}.`,
      );
    },
    onError: (err) => notifyError(err, 'issue this credit note'),
  });

  // Issuing burns a number out of a CAI range and cannot be taken back, so it says plainly what it
  // will do — including the things it will NOT do, which is what people actually worry about here.
  const confirm = form.onSubmit((v) => {
    const amountMinor = toMinorUnits(v.amount) ?? 0;
    const rangeName =
      ranges.options.find((o) => o.value === v.emissionPointId)?.label ?? 'the range you chose';
    close();
    modals.openConfirmModal({
      title: 'Issue this credit note?',
      children: (
        <Text size="sm">
          This prints a new numbered document from {rangeName} and links it to invoice {number}. It records{' '}
          {formatMoney(amountMinor, currency)} going back to the guest. Invoice {number} is not changed and
          nothing is deleted — both documents stay on the record, which is what the rules require. A credit
          note cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Issue it', cancel: 'Not yet' },
      confirmProps: { color: 'red' },
      onConfirm: () => issue.mutate(v),
    });
  });

  if (doc.type !== 'invoice') return null;

  if (remainingMinor <= 0) {
    return (
      <Text size="xs" c="dimmed">
        Fully credited
      </Text>
    );
  }

  const noRanges = !ranges.isLoading && !ranges.isError && ranges.options.length === 0;
  const noReasons = !reasons.isLoading && !reasons.isError && reasons.options.length === 0;

  return (
    <>
      <Button size="xs" variant="subtle" loading={issue.isPending} onClick={open}>
        Credit note
      </Button>
      <Modal
        opened={opened}
        onClose={close}
        title={`Credit note against invoice ${number}`}
        size="lg"
        closeOnClickOutside={false}
      >
        <form onSubmit={confirm}>
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              An invoice is never changed once it is printed. Giving money back is a separate numbered
              document that points at the original, and both stay on the record.
            </Text>

            {noRanges && (
              <Alert color="yellow" title="No credit note range registered">
                A credit note has to be numbered from its own CAI range. Add a number range on the other tab,
                set to print credit notes, before you can issue one.
              </Alert>
            )}
            {noReasons && (
              <Alert color="yellow" title="No reasons set up yet">
                Add a few reasons on the Settings screen — a returned item, a mistake on the bill — and they
                will appear here.
              </Alert>
            )}

            <Text size="sm">
              Invoice {number} was for {formatMoney(totalMinor, currency)}
              {creditedMinor > 0 && ` — ${formatMoney(creditedMinor, currency)} has already been credited`}.
            </Text>

            <EntitySelect
              entity="creditNoteRange"
              label="Which credit note range"
              description="Credit notes are numbered from their own CAI range, never from the invoice range"
              required
              value={form.getValues().emissionPointId || null}
              onChange={(v) => form.setFieldValue('emissionPointId', v ?? '')}
              error={form.errors.emissionPointId}
            />

            <NumberInput
              label="How much to give back"
              required
              min={0}
              decimalScale={2}
              fixedDecimalScale
              thousandSeparator=","
              hideControls
              description={`At most ${formatMoney(remainingMinor, currency)}`}
              {...form.getInputProps('amount')}
            />

            <EntitySelect
              entity="creditNoteReason"
              label="Why"
              required
              value={form.getValues().reasonCode || null}
              onChange={(v) => form.setFieldValue('reasonCode', v ?? '')}
              error={form.errors.reasonCode}
            />

            <TextInput
              label="What happened"
              description="A sentence for whoever reads this later. It is printed on the document."
              placeholder="Guest returned two bottles unopened"
              {...form.getInputProps('detail')}
            />
          </Stack>
          <Group justify="flex-end" mt="xl">
            <Button variant="default" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" color="red" disabled={noRanges}>
              Continue
            </Button>
          </Group>
        </form>
      </Modal>
    </>
  );
}

export function FiscalPage() {
  const locationId = useLocationId();
  const timezone = useLocationTimezone();
  const businessDate = useBusinessDate();
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [datesSeeded, setDatesSeeded] = useState(false);
  const documents = useAllDocuments();
  const creditNotes = useCreditNotes();

  /*
   * The register comes back 500 rows deep, newest first. For credit notes that is a couple of busy
   * weeks; the night the registers start issuing invoices at roughly two hundred a night it is two and
   * a half days, and an unfiltered list would quietly stop reaching back far enough — which is the
   * exact failure that made the over-credit refusal necessary in the first place.
   *
   * So it opens on today's trading day and the reader widens it deliberately. Today's TRADING day,
   * not today's calendar date: service runs past midnight, and a screen that rolls at midnight goes
   * blank at 00:01 while the bar is still full.
   */
  useEffect(() => {
    if (!datesSeeded && businessDate) {
      setFrom(businessDate);
      setTo(businessDate);
      setDatesSeeded(true);
    }
  }, [businessDate, datesSeeded]);

  // A day picked here is a day AT THE BAR, and a document records the exact instant in UTC. Read
  // off the browser's own midnight instead, "the 14th" viewed from Europe starts and ends six hours
  // early — dropping the evening of the 14th, which at a bar is the day.
  const dateFilter =
    from || to
      ? {
          from: from ? zonedDayRange(from, timezone).from : undefined,
          to: to ? zonedDayRange(to, timezone).to : undefined,
        }
      : undefined;

  return (
    <>
      <Title order={3} mb="md">
        Invoicing
      </Title>
      <Tabs defaultValue="points" keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="points">Number ranges</Tabs.Tab>
          <Tabs.Tab value="documents">Invoices issued</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="points">
          <RangeWarnings />
          <CustomerRtnThreshold />
          <CrudSection
            title="Number ranges"
            entityNoun="number range"
            description="Every register that prints invoices has its own CAI and its own block of numbers. Printing stops by itself when a block runs out or when the CAI expires — you will be warned here well before that happens."
            listPath={POINTS_PATH}
            updatePathTemplate={`${POINTS_PATH}/{id}`}
            defaults={{ locationId }}
            rowLabel={(r) => `the range for register ${String(r.emissionPointCode)}`}
            emptyMessage="Nothing here yet. Add the CAI and number range you were issued for each register that prints invoices."
            columns={[
              { accessor: 'emissionPointCode', title: 'Register code' },
              { accessor: 'documentType', title: 'Prints', enumGroup: 'documentType' },
              { accessor: 'caiCode', title: 'CAI', noWrap: true },
              {
                accessor: 'nextNumber',
                title: 'Next number',
                noWrap: true,
                // A bare counter ("42") matches nothing anyone can hold. The number that goes on
                // the paper is the whole correlativo, and that is what you check the pad against.
                // Formatted on the server now, beside the code that assigns it — this screen and
                // the register have to agree about what the next number IS, and two formatters is one
                // too many.
                render: (r) => String(r.correlativoNext ?? ''),
              },
              {
                accessor: 'remaining',
                title: 'Numbers left',
                // Counted on the server. It used to be worked out here as rangeEnd - nextNumber + 1
                // while a nightly job worked out a percentage a different way, and the two
                // disagreed about the same range in front of the same person.
                render: (r) => Number(r.remaining ?? 0).toLocaleString('en-US'),
              },
              // "Prints until" rather than the SAR term: the date matters because printing stops on
              // it, and saying what happens beats naming the field on the paperwork.
              { accessor: 'deadline', title: 'Prints until', unit: 'date' },
              {
                accessor: 'deviceId',
                title: 'Which register',
                // A range for invoices is only ever used by a register, and the register finds it by being
                // attached to it — registered and unattached, it prints nothing at all. An empty
                // cell said the field had not been filled in, not that the range is dead weight,
                // and the difference is discovered mid-service. A credit note range is picked by
                // hand on this screen, so having no register on one of those is not a fault — and
                // "chosen when you issue one" is only true of credit notes, since nothing on this
                // site issues a debit note. Saying it of a debit note range would be a reassurance
                // about a screen that does not exist.
                render: (r) =>
                  r.deviceId ? (
                    <EntityLabel entity="device" id={r.deviceId} />
                  ) : r.documentType === 'invoice' ? (
                    <Text size="sm" c="orange.8">
                      No register — prints nothing
                    </Text>
                  ) : r.documentType === 'creditNote' ? (
                    <Text size="sm" c="dimmed">
                      Chosen when you issue one
                    </Text>
                  ) : (
                    <Text size="sm" c="dimmed">
                      No register attached
                    </Text>
                  ),
              },
              {
                accessor: 'active',
                title: 'Status',
                render: (r) => (
                  <Badge color={r.active ? 'teal' : 'gray'} variant="light">
                    {r.active ? 'Printing' : 'Stopped'}
                  </Badge>
                ),
              },
            ]}
            rowActions={(row, refresh) => <IssuingToggle point={row} refresh={refresh} />}
            // The table and the banner above it now say the same thing about the same row. Without
            // this, the banner named a register that had stopped printing and the reader had to find
            // it among six rows of digits.
            rowBackgroundColor={(row) =>
              row.severity === 'blocked'
                ? 'var(--mantine-color-red-light)'
                : row.severity === 'warning'
                  ? 'var(--mantine-color-yellow-light)'
                  : undefined
            }
            fields={[
              {
                name: 'rtn',
                label: 'RTN',
                type: 'text',
                required: true,
                fixedAfterCreate: true,
                // Unvalidated until now, and it prints at the top of every invoice this range ever
                // produces. The only correction for a printed document is a credit note, so a typo
                // here is not a typo — it is a stack of legal documents with the wrong tax id on
                // them and nothing that can withdraw any of them.
                description: 'The RTN this CAI was issued to — 14 digits',
                validate: rtnProblem,
              },
              {
                name: 'businessName',
                label: 'Business name',
                type: 'text',
                required: true,
                fixedAfterCreate: true,
                description: 'Exactly as it should appear at the top of the invoice',
              },
              {
                name: 'caiCode',
                label: 'CAI',
                type: 'text',
                required: true,
                fixedAfterCreate: true,
                description: 'Fixed once saved. A new CAI is always a new range.',
              },
              {
                name: 'documentType',
                label: 'What this range prints',
                type: 'select',
                required: true,
                fixedAfterCreate: true,
                options: enumOptionsFor('documentType', ['invoice', 'creditNote', 'debitNote']),
                description:
                  'Credit notes are printed from their own separate range, never from the invoice range',
              },
              {
                name: 'establishmentCode',
                label: 'Establishment code',
                type: 'text',
                required: true,
                fixedAfterCreate: true,
                description: 'The three digits for this location, from your CAI paperwork',
              },
              {
                name: 'emissionPointCode',
                label: 'Register code',
                type: 'text',
                required: true,
                fixedAfterCreate: true,
                description: 'The three digits that identify this register',
              },
              // Left optional, an empty box was stored as "01" without a word said. Those two
              // digits are the middle of every number this range prints, and 01 is the invoice
              // code — so a credit note range left blank printed credit notes numbered as
              // invoices, on paper, under a CAI, with no way to take any of them back.
              {
                name: 'documentTypeCode',
                label: 'Document code',
                type: 'text',
                required: true,
                fixedAfterCreate: true,
                description:
                  'The two digits on your CAI paperwork for what this range prints — 01 is the invoice code, and a credit note range carries a different one.',
                validate: (value) =>
                  /^\d{2}$/.test(String(value ?? ''))
                    ? null
                    : 'Two digits, exactly as they appear on the paperwork',
              },
              {
                name: 'rangeStart',
                label: 'First number',
                type: 'number',
                required: true,
                fixedAfterCreate: true,
                description: 'Fixed once saved — printing starts here and counts up',
              },
              {
                name: 'rangeEnd',
                label: 'Last number',
                type: 'number',
                required: true,
                fixedAfterCreate: true,
                validate: (value, values) =>
                  Number(value) < Number(values.rangeStart)
                    ? 'The last number cannot be lower than the first'
                    : null,
              },
              {
                name: 'deadline',
                label: 'Prints until',
                type: 'date',
                required: true,
                description: 'This range cannot be used after this date, even if numbers are left',
              },
              {
                name: 'deviceId',
                label: 'Register',
                type: 'entity',
                resource: 'device',
                nullable: true,
                description:
                  'Which register prints from this range. A range for invoices prints nothing until a register is attached to it; a credit note range does not need one, because you pick it here.',
              },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="documents">
          <CrudSection
            title="Invoices issued"
            description="Everything printed here, newest first. An invoice is never changed after the fact — to give money back, issue a credit note from the invoice's own row and it will point back at the original."
            listPath={DOCUMENTS_PATH}
            listQuery={dateFilter}
            emptyMessage={
              dateFilter
                ? 'Nothing was printed on these dates. Widen the dates to look further back.'
                : 'Nothing printed yet. Invoices appear here as soon as the registers start issuing them.'
            }
            rowActions={(row) => (
              <>
                <DocumentCopyButton doc={row} />
                <CreditNoteButton doc={row} />
              </>
            )}
            actions={
              <Group gap="xs" wrap="nowrap" align="flex-end">
                <DateInput
                  label="From"
                  value={from}
                  onChange={setFrom}
                  valueFormat="D MMM YYYY"
                  placeholder="Any day"
                  clearable
                  size="xs"
                  w={150}
                  popoverProps={{ withinPortal: true }}
                />
                <DateInput
                  label="To"
                  value={to}
                  onChange={setTo}
                  valueFormat="D MMM YYYY"
                  placeholder="Any day"
                  clearable
                  size="xs"
                  w={150}
                  popoverProps={{ withinPortal: true }}
                />
              </Group>
            }
            columns={[
              { accessor: 'correlativo', title: 'Number', noWrap: true },
              {
                accessor: 'type',
                title: 'Kind',
                render: (r) => <KindCell row={r} documents={documents} creditNotes={creditNotes} />,
              },
              { accessor: 'issuedAt', title: 'Issued', unit: 'datetime' },
              { accessor: 'totalMinor', title: 'Total', unit: 'money', currencyAccessor: 'currency' },
              {
                accessor: 'customerName',
                title: 'Customer',
                // One answer, one column. Most guests give neither a name nor an RTN — that is a
                // walk-in, not a gap in the record — so two columns side by side were blank all
                // the way down and the eye had to read both to learn nothing. The number sits
                // under the name because the guests who do give one gave it to have it on the
                // invoice, and that is the pair you check together.
                render: (r) => {
                  const name = r.customerName ? String(r.customerName) : null;
                  const rtn = r.customerRtn ? String(r.customerRtn) : null;
                  if (!name && !rtn) return <Text c="dimmed">Walk-in</Text>;
                  return (
                    <div>
                      <Text size="sm">{name ?? 'No name given'}</Text>
                      {rtn && (
                        <Text size="xs" c="dimmed">
                          RTN {rtn}
                        </Text>
                      )}
                    </div>
                  );
                },
              },
              { accessor: 'deviceId', title: 'Register', reference: 'device' },
            ]}
          />
        </Tabs.Panel>
      </Tabs>
    </>
  );
}
